import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, User, MapPin, Car, Edit, CheckCircle, X, Clock, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TelecallerService } from '../../services/telecallerService';
import { maskPhoneLast4 } from '../../utils/phoneMask';

const AssignedLeads = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [isLogging, setIsLogging] = useState(false);
  const [callStateByLeadId, setCallStateByLeadId] = useState({});

  const { data: leadsData, isLoading, refetch } = useQuery({
    queryKey: ['telecallerAssignedLeads', user?.id],
    queryFn: () => TelecallerService.getAssignedLeads(user?.id),
    enabled: !!user?.id,
  });

  const leads = leadsData?.data || [];

  const handleCall = (lead) => {
    setSelectedLead(lead);
    setShowCallModal(true);
  };

  const handleEndCall = async () => {
    if (!selectedLead || !callStatus) {
      alert('Please select call status');
      return;
    }

    setIsLogging(true);
    try {
      const result = await TelecallerService.logCall(
        selectedLead.id,
        user?.id,
        callStatus,
        callNotes
      );

      if (result.success) {
        alert('Call logged successfully!');
        setShowCallModal(false);
        setSelectedLead(null);
        setCallNotes('');
        setCallStatus('');
        refetch();
      } else {
        alert('Failed to log call: ' + result.error);
      }
    } catch (error) {
      alert('Error logging call: ' + error.message);
    } finally {
      setIsLogging(false);
    }
  };

  const callCustomer = (leadId, phoneNumber) => {
    const k = String(leadId);
    setCallStateByLeadId((m) => ({ ...m, [k]: { phase: 'calling', statusText: 'calling', reason: '' } }));
    TelecallerService.sarvClickToCall(user?.phone || user?.contact_number, phoneNumber)
      .then((res) => {
        if (!res?.success) {
          const reason =
            res?.data?.sarv?.message ||
            res?.data?.sarv?.error ||
            res?.error ||
            'Click-to-call failed';
          setCallStateByLeadId((m) => ({ ...m, [k]: { phase: 'failed', statusText: 'failed', reason: String(reason) } }));
          return;
        }
        const statusText = String(res?.data?.sarv?.status || 'success').toLowerCase();
        setCallStateByLeadId((m) => ({ ...m, [k]: { phase: 'success', statusText, reason: '' } }));
      })
      .catch((e) =>
        setCallStateByLeadId((m) => ({
          ...m,
          [k]: { phase: 'failed', statusText: 'failed', reason: String(e?.message || e) },
        }))
      );
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'confirmed') return 'bg-green-100 text-green-700';
    if (s === 'not_answer') return 'bg-yellow-100 text-yellow-700';
    if (s === 'not_interested') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Assigned Leads</h1>
          <p className="text-gray-600 mt-1">View and manage your assigned leads</p>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leads...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <User size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No assigned leads</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((lead) => (
            <div key={lead.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(lead.status)}`}>
                  {lead.status || 'Pending'}
                </span>
              </div>

              <div className="space-y-2 text-sm mb-4">
                {lead.address && (
                  <div className="flex items-start space-x-2">
                    <MapPin size={16} className="text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{lead.address}</span>
                  </div>
                )}
                {lead.car_model && (
                  <div className="flex items-center space-x-2">
                    <Car size={16} className="text-gray-400" />
                    <span className="text-gray-600">{lead.car_model}</span>
                  </div>
                )}
                {lead.car_number && (
                  <div className="flex items-center space-x-2">
                    <Car size={16} className="text-gray-400" />
                    <span className="text-gray-600">{lead.car_number}</span>
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => callCustomer(lead.id, lead.contact_number)}
                  className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center space-x-1"
                >
                  <Phone size={16} />
                  <span>Call</span>
                </button>
                <button
                  onClick={() => handleCall(lead)}
                  className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-1"
                >
                  <PhoneCall size={16} />
                  <span>Log</span>
                </button>
              </div>

              {(() => {
                const st = callStateByLeadId[String(lead.id)];
                if (!st || st.phase === 'idle') return null;
                return (
                <div className="mt-2 text-xs">
                  <span className="font-semibold text-gray-700">Call Status:</span>{' '}
                  <span
                    className={
                      st.phase === 'success'
                        ? 'text-green-700'
                        : st.phase === 'failed'
                          ? 'text-red-700'
                          : 'text-yellow-700'
                    }
                  >
                    {st.statusText || st.phase}
                  </span>
                  {st.phase === 'failed' && st.reason ? (
                    <div className="text-red-600 mt-1">{String(st.reason)}</div>
                  ) : null}
                </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Call Modal */}
      {showCallModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Log Call</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Customer</p>
                <p className="font-semibold text-gray-900">{selectedLead.name || 'Unknown'}</p>
                <p className="text-sm text-gray-600">{maskPhoneLast4(selectedLead.contact_number) || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Status *</label>
                <select
                  value={callStatus}
                  onChange={(e) => setCallStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select status</option>
                  <option value="answered">Answered</option>
                  <option value="not_answer">Not Answer</option>
                  <option value="callback">Callback</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Notes</label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Add call notes..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCallModal(false);
                    setSelectedLead(null);
                    setCallNotes('');
                    setCallStatus('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEndCall}
                  disabled={!callStatus || isLogging}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {isLogging ? 'Logging...' : 'Log Call'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedLeads;

